import React, { useState, useEffect } from 'react';
import api, { useAuth } from '@/lib/api';
import CustomExportBuilder from '@/components/CustomExportBuilder';
import DbSizeWidget from '@/components/DbSizeWidget';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Download, Trash2, RefreshCw, Loader2, FileSpreadsheet, FileArchive, Zap, ShieldAlert,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconFor = (name) => {
  if (name.endsWith('.zip')) return <FileArchive className="h-4 w-4 text-amber-600" />;
  return <FileSpreadsheet className="h-4 w-4 text-emerald-600" />;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminExportsPage = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [generators, setGenerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // generator key currently running
  const [selectedGen, setSelectedGen] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isAuthorized = user?.role === 'super_admin' || user?.role === 'admin';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [filesRes, gensRes] = await Promise.all([
        api.get('/admin/exports'),
        api.get('/admin/exports/generators/list'),
      ]);
      setFiles(filesRes.data?.files || []);
      setGenerators(gensRes.data?.generators || []);
    } catch (e) {
      toast.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) fetchAll();
  }, [isAuthorized]);

  const handleDownload = async (filename) => {
    try {
      const token = sessionStorage.getItem('clt_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/exports/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const handleGenerate = async () => {
    if (!selectedGen) return;
    setRunning(selectedGen);
    try {
      const res = await api.post(`/admin/exports/generate/${selectedGen}`);
      toast.success(`Generated: ${res.data?.filename} (${res.data?.size_human})`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setRunning(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete);
    try {
      await api.delete(`/admin/exports/${encodeURIComponent(confirmDelete)}`);
      toast.success(`Deleted ${confirmDelete}`);
      setConfirmDelete(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="flex items-center gap-3 py-12 justify-center text-muted-foreground">
            <ShieldAlert className="h-6 w-6" />
            <span>Super Admin / Admin only.</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-exports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Exports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate and download bulk Excel / CSV / ZIP exports. Files are stored on the server until you delete them.
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" size="sm" disabled={loading} data-testid="exports-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Cluster storage health — early warning for the 512 MB Atlas quota */}
      <DbSizeWidget />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="h-5 w-5 text-amber-500" />
            Generate New Export
          </CardTitle>
          <CardDescription>
            Pick a pre-configured export. Generation may take 30–120s depending on data size.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3 flex-wrap">
          <Select value={selectedGen} onValueChange={setSelectedGen}>
            <SelectTrigger className="w-[420px] max-w-full" data-testid="exports-generator-select">
              <SelectValue placeholder="Select an export to generate..." />
            </SelectTrigger>
            <SelectContent>
              {generators.map((g) => (
                <SelectItem key={g.key} value={g.key} data-testid={`gen-${g.key}`}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleGenerate}
            disabled={!selectedGen || running}
            data-testid="exports-generate-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
            {running ? 'Generating…' : 'Generate'}
          </Button>
        </CardContent>
      </Card>

      <CustomExportBuilder onGenerated={fetchAll} />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Files ({files.length})</CardTitle>
          <CardDescription>Click a row to download. Delete files you no longer need.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : files.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              No export files yet. Generate one above.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.filename} data-testid={`export-row-${f.filename}`}>
                    <TableCell>{iconFor(f.filename)}</TableCell>
                    <TableCell className="font-medium">{f.filename}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{f.size_human}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(f.modified_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(f.filename)}
                          data-testid={`download-${f.filename}`}
                        >
                          <Download className="h-4 w-4 mr-1.5" />
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(f.filename)}
                          disabled={deleting === f.filename}
                          data-testid={`delete-${f.filename}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deleting === f.filename ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this export?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{confirmDelete}</span> will be permanently removed from the server.
              You can always re-generate it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-ok"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminExportsPage;
